import React, { useCallback, useMemo, useState } from "react";
import Select, { components, MultiValue, GroupBase, ValueContainerProps, SingleValue, OptionProps, MenuListProps } from "react-select";
import Icon from "../../../../../components/icons/icon/icon";
import "./multi-nested-selector.scss";
import { Checkbox, CHECKBOX_SIZES } from "../../../../../components/inputs/checkbox/checkbox";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { TRISTATE_CHECKBOX_VALUES, TristateCheckbox } from "../../../../../components/inputs/tristate-checkbox/tristate-checkbox";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import IconDropDownMediumArrow from "../../../../../components/icons/icon-drop-down-medium-arrow/icon-drop-down-medium-arrow";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import useInputValidation from "../../../../../hooks/input-validation-hook";


/*
    <MultiNestedSelector options={
    [
        {
            label: "FedEx",
            value: "fedex",
            options: [
                { label: "Fedex service 1", value: "fedex-service1" },
                { label: "Fedex service 2", value: "fedex-service2" },
                { label: "Cheapest", value: "cheapest-service" },
            ],
        },
        {
            label: "USPS",
            value: "usps",
            options: [
                { label: "USPS service 1", value: "usps-service-1" },
                { label: "USPS service 2", value: "usps-service-2" },
                { label: "Cheapest", value: "cheapest-service" },
            ],
        },

    ]
}
    value={selectedValues}
    onChange={(e) => {
        console.log(e)
        setSelectedValues(e as IOption[])
    }}
/>
*/

export interface IOption {
    label: string;
    value: string;
    _value?: string;
    options?: IOption[];
    isCategory?: boolean;
    hidden?: boolean;
}

const SELECT_ALL = 'select-all';

const selectAllCategory = {
    label: "Select All",
    value: SELECT_ALL,
    isCategory: true,
    isSelectAll: true,
    options: [{ label: "", value: "select/unselect all", isDisabled: true }]
};

interface IMultiNestedSelectorProps {
    options: IOption[];
    placeholder?: string;
    value?: IOption[];
    onChange?: (e: IOption[]) => void;
    isLoading?: boolean;
    required?: boolean;
    validationGroup?: IValidationGroup;
}

/** To ensure options under each category have unique values */
const indexOptions = (options: IOption[]): IOption[] => {
    return options.map((category) => ({
        ...category,
        options: category.options?.map((child) => ({
            ...child,
            _value: child.value,
            value: `${category.value}.${child.value}`,
        })) || [],
    }));
};

/** Put option under its parent category, remove _value */
const convertFlatIndexedValueToNestedNormalFormat = (flatIndexedValue: IOption[], allIndexedOptions: IOption[]): IOption[] => {
    return allIndexedOptions
        .map(category => ({
            ...category,
            options: category.options ? category.options.filter(child => flatIndexedValue.some(sel => sel.value === child.value)).map(sel => {
                return {
                    label: sel.label,
                    value: sel._value || '',
                };
            }) : [],
        }))
        .filter(category => category.options && category.options.length > 0);
};

const convertNestedToFlat = (options: IOption[]): IOption[] => {
    return options.flatMap(category => category.options || []);
};

/** Custom value component for react-select */
const ValueContainer: React.FC<ValueContainerProps<IOption> & { allIndexedOptionsFlat: IOption[] }> = ({ children, allIndexedOptionsFlat, ...props }) => {
    const selectedOptions = props.getValue() as IOption[];
    let [, input] = children as any;

    let selectedText = '';

    if (allIndexedOptionsFlat.length === selectedOptions.length) {
        selectedText = "All available carriers and service levels";
    } else {
        if (selectedOptions.length > 0) {
            const selectedCategories: Record<string, IOption[]> = {};
    
            selectedOptions.forEach((option) => {
                const categoryKey = option.value.split(".")[0];
                if (!selectedCategories[categoryKey]) {
                    selectedCategories[categoryKey] = [];
                }
                selectedCategories[categoryKey].push(option);
            });
    
            selectedText = Object.entries(selectedCategories)
                .map(([categoryValue, selected]) => {
                    const category = props.selectProps.options.find(
                        (opt: any) => opt.value === categoryValue
                    ) as IOption;
    
                    if (!category) {
                        return null;
                    };
                    if (category.options?.length === selected.length) {
                        return `${category.label} (all service levels)`;
                    }
                    return `${category.label} (${selected.length} ${selected.length > 1 ? "service levels" : "service level"})`;
                })
                /* Remove null values and join with comma */
                .filter(Boolean)
                .join(", ");
        }    
    }

    
    return (
        <components.ValueContainer {...props}>
            <span className="select-dropdown-multi-nested-value-wrapper">
                <FontBody2 color={FONT_COLOR.ACCENT2}>
                    {input}
                    {!input.props.value && selectedText}
                </FontBody2>
            </span>
        </components.ValueContainer>
    );
};

/** Custom option component for react-select */
const Option: React.FC<OptionProps<IOption, boolean, GroupBase<IOption>>> = ({ ...props }) => {
    const { data, isSelected, selectOption } = props;

    if (data.value === "placeholder") {
        return null;
    }

    return (
        <components.Option {...props}>
            <span className="option-checkbox">
                <FontBody2>
                    <Checkbox
                        label={data.label}
                        checked={isSelected}
                        onChange={() => selectOption(data)}
                        size={CHECKBOX_SIZES.SMALL}
                    />
                </FontBody2>
            </span>
        </components.Option>
    );
};

/** Custom menu list component for react-select */
const CustomMenuList: React.FC<MenuListProps<IOption, boolean>> = ({ children, ...props }) => {
    return (
        <div className="multi-nested-selector-select-dropdown-container">
            <components.MenuList {...props} selectProps={props.selectProps}>
                {children}
            </components.MenuList>
        </div>
    );
};

const MultiNestedSelector: React.FC<IMultiNestedSelectorProps> = ({ options, value = [], placeholder, onChange, isLoading, required, validationGroup }) => {
    const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
    const [inputValue, setInputValue] = useState("");

    const indexedValue = indexOptions(value);
    const indexedOptions = useMemo(() => indexOptions(options), [options]);
    const allIndexedOptionsFlat = useMemo(() => convertNestedToFlat(indexedOptions), [indexedOptions]);
    const isAllOptionsSelected = allIndexedOptionsFlat.length > 0 &&
        allIndexedOptionsFlat.every(opt => indexedValue.some(category => category.options?.some(sel => sel.value === opt.value)));

    const handleSelectAll = () => {
        if (isAllOptionsSelected) {
            onChange?.([]);
        } else {
            onChange?.(convertFlatIndexedValueToNestedNormalFormat(allIndexedOptionsFlat, indexedOptions));
        }
    };

    const handleCategoryChange = (categoryValue: string) => {
        const category = indexedOptions.find(opt => opt.value === categoryValue);
        if (!category) {
            return;
        }

        const categoryOptions = category.options ?? [];
        const isAllSelected = categoryOptions.every(opt => indexedValue.some(sel => sel.options?.some(child => child.value === opt.value)));

        if (isAllSelected) {
            onChange?.(value.filter(cat => cat.value !== categoryValue));
            return;
        }
        return onChange?.([...value.filter(category => category.value !== categoryValue), {
            ...category, options: categoryOptions.map(opt => ({ label: opt.label, value: opt._value || '' }))
        }]);
    };

    const handleChange = (newValue: MultiValue<IOption> | SingleValue<IOption>) => {
        if (!Array.isArray(newValue)) {
            return;
        }

        const newNestedValue = convertFlatIndexedValueToNestedNormalFormat(newValue, indexedOptions);
        onChange?.(newNestedValue);
    };

    const toggleCategoryExpansion = (categoryValue: string) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryValue]: !prev[categoryValue],
        }));
    };

    /** Custom group heading component for react-select */
    const customGroupHeading = (props: any) => {
        const { data } = props;
        const categoryValue = data.value;
        const categoryOptions = indexedOptions.find(opt => {
            return opt.value === categoryValue;
        })?.options || [];
        const isExpanded = expandedCategories[categoryValue] || false;

        const isAllInCategorySelected = categoryOptions.length > 0 &&
            categoryOptions.every(opt => indexedValue.some(sel => sel.options?.some(child => {
                return child.value === opt.value;
            })));

        const isCategoryPartiallySelected = categoryOptions.some(opt => indexedValue.some(sel => sel.options?.some(child => {
            return child.value === opt.value
        })));

        return (
            <span className="category-wrapper" onClick={(e) => {
                e.stopPropagation();
                /* Wrap in setTimeout to prevent conflicts with checkbox click event */
                setTimeout(() => {
                    toggleCategoryExpansion(categoryValue);
                }, 0);
            }}>
                <span className={`category-toggle ${isExpanded ? 'expanded' : ''}`}>
                    {!data.isSelectAll && <Icon Component={IconDropDownMediumArrow} />}
                </span>
                {data.isSelectAll ? (
                    <span onClick={handleSelectAll}>
                        <FontBody2 color={FONT_COLOR.ACCENT1} variant={[FONT_VARIANT.UNDER_LINE]}>
                            {isAllOptionsSelected ? 'Unselect All' : 'Select All'}
                        </FontBody2>
                    </span>
                ) : (
                    <FontBody2>
                        <TristateCheckbox
                            label={data.label}
                            value={isAllInCategorySelected ? TRISTATE_CHECKBOX_VALUES.CHECKED :
                                isCategoryPartiallySelected ? TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED :
                                    TRISTATE_CHECKBOX_VALUES.UNCHECKED}
                            onChange={() => handleCategoryChange(categoryValue)}
                            size={CHECKBOX_SIZES.SMALL}
                        />
                    </FontBody2>
                )}
            </span>
        );
    };

    const isValid = useInputValidation({ value: value?.length ? value.length : '', validationGroup, required });

    /** Memoize ValueContainer to prevent unnecessary re-renders as it receives indexedOptions as prop */
    const MemoizedValueContainer = useCallback((props: any) => (
        <ValueContainer {...props} allIndexedOptionsFlat={allIndexedOptionsFlat} />
    ), [allIndexedOptionsFlat]);
    return (
        <div className={`nested-multi-select-container ${!isValid ? 'invalid' : ''}`}
        >
            <Select
                options={indexedOptions.length > 0 ? [
                    selectAllCategory,
                    ...indexedOptions.map(category => ({
                            ...category,
                            options: expandedCategories[category.value]
                                ? category.options

                                /** We need to persist value to use it upon filtering, the checkbox will be hidden by css when aria-disabled=true (set by isDisabled=true) */
                                : category.options?.map((option) => (
                                    {label: '', value: option.value, isDisabled: true}
                                ))
                        })
                    )
                ] : []}
                filterOption={(option, searchQuery) => {
                    if (!searchQuery) {
                        return true;
                    }

                    /** keep select/unselect all visible */
                    if (option.value === 'select/unselect all') {
                        return true;
                    }

                    const searchLower = searchQuery.toLowerCase();
                    return indexedOptions.some(category =>
                        category.label.toLowerCase().includes(searchLower) &&
                        category.options?.some(opt => opt.value === option.value)
                    );
                }}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={convertNestedToFlat(indexedValue)}
                inputValue={inputValue}
                onChange={handleChange}
                onInputChange={(value, { action }) => {
                    if (action === "input-change") {
                        setInputValue(value);
                    }
                    if (action === "menu-close") {
                        setInputValue("");
                    }
                }}
                placeholder={placeholder}
                components={{
                    Option,
                    ValueContainer: MemoizedValueContainer,
                    GroupHeading: customGroupHeading,
                    MenuList: CustomMenuList,
                }}
                isLoading={isLoading}
                isClearable={false}
                menuShouldScrollIntoView={false}
                className="nested-multi-select-dropdown-container"
                classNamePrefix="select-dropdown-nested"
            />
        </div>
    );
};

export default MultiNestedSelector;
